import type {
  BookAppointmentInput,
  CreateSaleInput,
} from '@/features/actions/schemas/actionSchemas'
import type { ActionSubject } from '@/features/actions/types'
import { MACHINES } from '@/features/actions/demo/catalog'
import { demoBilling } from '@/features/actions/demo/demoBilling'
import {
  SLOT_MINUTES,
  addDays,
  fromMinutes,
  oneHourAfter,
  toMinutes,
  today,
} from '@/features/scheduler/schedulerDates'
import type { AppointmentCreated, CheckedIn } from '@/api/schedulerApi'
import type { Appointment, CalendarBoard, Device } from '@/features/scheduler/types'
import { ApiError } from '@/api/errors'

const NAMES = [
  'Jessica Walter',
  'Lana Hvy Dinh',
  'Anna Ziegler',
  'Adama Sumaila',
  'Adel Ibrahim',
  'Ahlem Gerges',
  'Aida Jakupovic',
  'Akua Gyamfua',
  'Alexandra Leotta',
  'Barbara Schwab',
  'Birgit Riemer',
  'Carolin Haag',
  'Claudia März',
  'Daniela Gareis',
  'Eleni Papadopoulou',
  'Elisa Mueller',
]

const DEVICES: Device[] = MACHINES.map((name, index) => ({
  id: String(index + 1),
  name,
  position: index + 1,
}))

const OPENING_HOURS = { start: '08:00', end: '20:00' }

const STATUS_LABELS: Record<string, string> = {
  booked: 'Gebucht',
  checked_in: 'Eingecheckt',
  done: 'Erledigt',
  cancelled: 'Storniert',
  no_show: 'Nicht erschienen',
}

const KIND_LABELS: Record<string, string> = {
  trial: 'Probetermin',
  treatment: 'Behandlungstermin',
}

const CREDIT_LABELS: Record<string, string> = {
  consumed: 'Verbraucht',
  reserved: 'Reserviert',
  open: 'Offen',
}
const SEED_RANGE = 14

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

function statusFor(offset: number, slot: number): string {
  if (offset > 0) return slot % 7 === 3 ? 'cancelled' : 'booked'
  if (offset === 0) return slot % 4 === 1 ? 'checked_in' : 'booked'
  if (slot % 6 === 4) return 'no_show'
  if (slot % 6 === 5) return 'cancelled'
  return 'done'
}

function seedAppointments(): Appointment[] {
  const base = today()
  const appointments: Appointment[] = []
  let counter = 0

  for (let offset = -SEED_RANGE; offset <= SEED_RANGE; offset++) {
    const date = addDays(base, offset)
    // Sundays are closed, Saturdays quiet.
    const weekday = new Date(`${date}T12:00:00`).getDay()
    if (weekday === 0) continue

    const perDay = weekday === 6 ? 3 : 5 + (Math.abs(offset) % 3)

    for (let slot = 0; slot < perDay; slot++) {
      const step = counter++
      const hour = 8 + ((slot * 2 + offset + 14) % 11)
      const quarter = ((((offset + (slot % MACHINES.length)) % 4) + 4) % 4) * SLOT_MINUTES
      const start = fromMinutes(hour * 60 + quarter)

      const status = statusFor(offset, slot)
      const kind = step % 11 === 0 ? 'trial' : 'treatment'
      const creditState = status === 'checked_in' ? 'consumed' : 'reserved'

      appointments.push({
        id: `3${String(step).padStart(3, '0')}`,
        deviceId: DEVICES[slot % DEVICES.length].id,
        deviceName: DEVICES[slot % DEVICES.length].name,
        customerId: String(500 + (step % NAMES.length)),
        customerName: NAMES[step % NAMES.length],
        date,
        start,
        end: oneHourAfter(start),
        kind,
        kindLabel: KIND_LABELS[kind] ?? null,
        status,
        statusLabel: STATUS_LABELS[status] ?? null,
        creditState,
        creditStateLabel: CREDIT_LABELS[creditState] ?? null,
      })
    }
  }

  return appointments
}

const appointments = seedAppointments()

for (const appointment of appointments) {
  if (appointment.creditState === 'reserved' && appointment.status === 'booked') {
    demoBilling.reserveCredit(appointment.customerId)
  }
}

const WIDERRUF_TEXT =
  'Ich verlange ausdrücklich, dass vor Ende der Widerrufsfrist mit der Behandlung begonnen wird. ' +
  'Mir ist bekannt, dass mein Widerrufsrecht bei vollständiger Vertragserfüllung erlischt.'

function seedLinkPurchase() {
  const base = today()
  const upcoming = appointments
    .filter((appointment) => appointment.status === 'booked' && appointment.date >= base)
    .sort((a, b) => `${a.date} ${a.start}`.localeCompare(`${b.date} ${b.start}`))
  const customerId = upcoming[0]?.customerId

  return {
    appointmentIds: new Set(
      upcoming
        .filter((appointment) => appointment.customerId === customerId)
        .slice(0, 2)
        .map((appointment) => appointment.id),
    ),
    windowEndsOn: addDays(base, 14),
    consentRecorded: false,
  }
}

const linkPurchase = seedLinkPurchase()

export const demoScheduler = {
  fetchContacts: async (): Promise<ActionSubject[]> => {
    await wait(120)
    return NAMES.map((name, index) => ({ id: String(500 + index), name }))
  },

  fetchCalendar: async (from: string, to: string): Promise<CalendarBoard> => {
    await wait(220)

    return {
      // The real endpoint resolves these at the location's zone; midnight to
      // midnight is the same thing for a whole-day range.
      from: `${from}T00:00:00+02:00`,
      to: `${addDays(to, 1)}T00:00:00+02:00`,
      timezone: 'Europe/Berlin',
      slotMinutes: 15,
      startStepMinutes: 15,
      allowedDurationMinutes: [60],
      openingHours: { ...OPENING_HOURS },
      devices: DEVICES.map((device) => ({ ...device })),
      appointments: appointments
        .filter((appointment) => appointment.date >= from && appointment.date <= to)
        .map((appointment) => ({ ...appointment })),
    }
  },

  create: async (input: BookAppointmentInput): Promise<AppointmentCreated> => {
    await wait(320)

    const { kind } = input
    const appointment: Appointment = {
      id: `3${String(appointments.length + 500).padStart(3, '0')}`,
      deviceId: input.deviceId,
      deviceName: (DEVICES.find((device) => device.id === input.deviceId) ?? DEVICES[0]).name,
      customerId: input.subjectId,
      customerName: NAMES[(Number(input.subjectId) - 500 + NAMES.length) % NAMES.length],
      date: input.date,
      start: input.start,
      end: fromMinutes(toMinutes(input.start) + input.durationMinutes),
      kind,
      kindLabel: KIND_LABELS[kind] ?? null,
      status: 'booked',
      statusLabel: STATUS_LABELS.booked,
      creditState: 'reserved',
      creditStateLabel: CREDIT_LABELS.reserved,
    }

    appointments.push(appointment)
    demoBilling.reserveCredit(appointment.customerId)
    // The demo has no funnel behind it, so a booking never moves a stage.
    return {
      appointment: { ...appointment },
      credits: demoBilling.creditsOf(appointment.customerId),
      stageChanged: false,
      previousStage: null,
      pipeline: null,
    }
  },

  checkIn: async (appointmentId: string, widerrufConsent = false): Promise<CheckedIn> => {
    await wait(260)

    const appointment = appointments.find((candidate) => candidate.id === appointmentId)
    if (!appointment) {
      throw new ApiError({ message: 'Dieser Termin wurde nicht gefunden.', status: 404 })
    }
    if (appointment.status === 'checked_in') {
      throw new ApiError({
        message: 'Dieser Termin wurde bereits eingecheckt.',
        status: 409,
        code: 'CREDIT_ALREADY_CONSUMED',
      })
    }
    if (linkPurchase.appointmentIds.has(appointment.id) && !linkPurchase.consentRecorded) {
      if (!widerrufConsent) {
        const message =
          'Vor der ersten Behandlung aus diesem Kauf braucht es die ausdrückliche Zustimmung zum Beginn innerhalb der Widerrufsfrist.'
        const details = {
          text: WIDERRUF_TEXT,
          textVersion: 'v1',
          windowEndsOn: linkPurchase.windowEndsOn,
        }
        throw new ApiError({
          message,
          status: 409,
          code: 'WIDERRUF_CONSENT_REQUIRED',
          body: { code: 'WIDERRUF_CONSENT_REQUIRED', message, details },
        })
      }
      linkPurchase.consentRecorded = true
    }

    appointment.status = 'checked_in'
    appointment.statusLabel = STATUS_LABELS.checked_in
    appointment.creditState = 'consumed'
    appointment.creditStateLabel = CREDIT_LABELS.consumed
    demoBilling.consumeCredit(appointment.customerId)

    return {
      appointmentId: appointment.id,
      status: appointment.status,
      statusLabel: appointment.statusLabel,
      checkedInAt: new Date().toISOString(),
      checkedInBy: 'Admin',
      creditState: appointment.creditState,
      creditStateLabel: appointment.creditStateLabel,
      credits: demoBilling.creditsOf(appointment.customerId),
    }
  },

  /** A sale belongs to the customer, not the board, so there is nothing here to change. */
  createSale: async (_input: CreateSaleInput): Promise<void> => {
    await wait(320)
  },
}
